import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { toast } from "react-toastify";
import styles from "./ComparePages.module.css";

const SITE_URL = "https://www.easexpense.com";

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

const ComparePages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [vendorA, setVendorA] = useState("");
  const [planA, setPlanA] = useState("");
  const [vendorB, setVendorB] = useState("");
  const [planB, setPlanB] = useState("");

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/compare_pages?limit=200`,
        { credentials: "include" },
      );
      const json = await res.json();
      if (json?.status === 200) setItems(json.data?.items || []);
      else toast.error(json?.message || "Failed to load");
    } catch (e) {
      console.error("[compare_pages]", e);
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleGenerate = async () => {
    if (!vendorA.trim() || !vendorB.trim()) {
      toast.warning("Both vendors are required");
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/compare_pages/generate`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vendorA: vendorA.trim(),
            planA: planA.trim() || undefined,
            vendorB: vendorB.trim(),
            planB: planB.trim() || undefined,
          }),
        },
      );
      const json = await res.json();
      if (json?.success) {
        toast.success(
          `Generated: ${json.data?.slug || "page"} — IndexNow ping fired`,
        );
        setVendorA("");
        setPlanA("");
        setVendorB("");
        setPlanB("");
        fetchList();
      } else {
        toast.error(json?.message || "Generation failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Compare Pages</h1>
          <div className={styles.subtitle}>
            SEO comparison landing pages. Claude generates the page from a
            (Vendor A, Plan A) vs (Vendor B, Plan B) pair, then IndexNow pings
            Bing immediately + Google picks it up via sitemap on the next
            revalidate.
          </div>
        </div>
        <Button
          onClick={fetchList}
          startIcon={<RefreshOutlinedIcon />}
          sx={{ textTransform: "none", color: "#607084" }}
        >
          Refresh
        </Button>
      </div>

      {/* Generator form */}
      <div className={styles.formCard}>
        <h3 className={styles.formTitle}>Generate a new comparison</h3>
        <p className={styles.formSub}>
          Use specific plan names where possible — "Business Starter", "E3",
          "Standard" — so the comparison rows have real prices to anchor on.
        </p>

        <div className={styles.formGrid}>
          <div className={styles.formRow}>
            <TextField
              size="small"
              label="Vendor A"
              placeholder="Google Workspace"
              value={vendorA}
              onChange={(e) => setVendorA(e.target.value)}
              disabled={generating}
            />
            <TextField
              size="small"
              label="Plan A (optional)"
              placeholder="Business Starter"
              value={planA}
              onChange={(e) => setPlanA(e.target.value)}
              disabled={generating}
            />
          </div>
          <div className={styles.formRow}>
            <TextField
              size="small"
              label="Vendor B"
              placeholder="Microsoft 365"
              value={vendorB}
              onChange={(e) => setVendorB(e.target.value)}
              disabled={generating}
            />
            <TextField
              size="small"
              label="Plan B (optional)"
              placeholder="Business Basic"
              value={planB}
              onChange={(e) => setPlanB(e.target.value)}
              disabled={generating}
            />
          </div>
        </div>

        <div className={styles.generateBtnRow}>
          <span className={styles.warningHint}>
            Generation takes ~30–60s (Claude). The page goes live + indexed
            immediately on success.
          </span>
          <Button
            className={styles.generateBtn}
            startIcon={
              generating ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <AutoAwesomeOutlinedIcon />
              )
            }
            disabled={generating}
            onClick={handleGenerate}
          >
            {generating ? "Generating…" : "Generate page"}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h3 className={styles.tableHeaderTitle}>
            Published pages · {items.length}
          </h3>
        </div>
        {loading ? (
          <div className={styles.loaderWrap}>
            <CircularProgress />
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            No comparison pages yet. Generate the first one above.
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Title / Slug</th>
                <th>Vendors</th>
                <th>Keyword</th>
                <th>Published</th>
                <th>Views</th>
                <th>Indexed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const url = `${SITE_URL}/compare/${p.slug}`;
                return (
                  <tr key={p.id}>
                    <td>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.slugLink}
                      >
                        {p.title || p.slug}
                      </a>
                      <div className={styles.muted}>/compare/{p.slug}</div>
                    </td>
                    <td>
                      <div>
                        <strong>{p.vendor_a}</strong>
                        {p.plan_a ? ` · ${p.plan_a}` : ""}
                      </div>
                      <div className={styles.muted}>
                        vs <strong>{p.vendor_b}</strong>
                        {p.plan_b ? ` · ${p.plan_b}` : ""}
                      </div>
                    </td>
                    <td className={styles.muted}>
                      {p.target_keyword || "—"}
                    </td>
                    <td>{formatDate(p.published_at)}</td>
                    <td>{p.views ?? 0}</td>
                    <td>
                      <span
                        className={`${styles.pill} ${p.indexnow_pinged_at ? styles.indexedPill : styles.notIndexedPill}`}
                      >
                        {p.indexnow_pinged_at ? "Pinged" : "Pending"}
                      </span>
                    </td>
                    <td>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                      >
                        <OpenInNewOutlinedIcon
                          sx={{ color: "#134f8b", fontSize: 18 }}
                        />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ComparePages;
